import { Flex, Image, theme } from 'antd';

import fallbackImage from '@assets/fallbackImage.jpg';
import ColorDot from '@components/modal/objectPickerModal/ColorDot';
import BaseText from '@components/typography/BaseText';
import { useThumbnailUrl } from '@hooks/useThumbnailUrl';
import { isSystemNode } from '@utils/taxonomyTree';

const eyebrowStyle = { textTransform: 'uppercase' };

/**
 * Identity block at the top of the object details panel: "Object details" eyebrow,
 * thumbnail, name with its tree colour, ancestor path, description and aliases.
 *
 * @param {object} object - a taxonomy node from GET /v2/taxonomy/nodes
 * @param {string} path - pre-built ancestor path, see `buildObjectPath`
 */
const ObjectSummary = ({ object, path }) => {
	const thumbnailUrl = useThumbnailUrl(object?.thumbnail);
	const { token } = theme.useToken();

	const isSystem = isSystemNode(object);
	const aliases = object?.aliases ?? [];
	const showAliases = isSystem && aliases.length > 0;
	const imageStyle = { borderRadius: token.borderRadiusSM, objectFit: 'cover' };

	return (
		<Flex vertical gap={12}>
			<BaseText type="secondary" size="xs" strong style={eyebrowStyle}>
				Object details
			</BaseText>

			<Flex gap={10} align="center">
				<Image
					src={thumbnailUrl ?? fallbackImage}
					fallback={fallbackImage}
					preview={false}
					width={56}
					height={56}
					alt=""
					style={imageStyle}
				/>
				<Flex vertical style={{ minWidth: 0 }}>
					<Flex align="center" gap={8}>
						<ColorDot color={object?.color} size={10} shape="round" />
						<BaseText strong ellipsis>
							{object?.name}
						</BaseText>
					</Flex>
					<BaseText type="secondary" size="xs">
						{path}
					</BaseText>
				</Flex>
			</Flex>

			{object?.description && (
				<BaseText type="secondary" size="sm">
					{object.description}
				</BaseText>
			)}

			{showAliases && (
				<BaseText type="success" size="xs">
					{aliases.join(' · ')}
				</BaseText>
			)}
		</Flex>
	);
};

export default ObjectSummary;
